'use client';

import { EmptyValue } from './empty-value';

interface JsonValueProps {
  value: any;
}

const RISK_LEVELS = [
  { id: 'faible', label: 'Faible' },
  { id: 'modere', label: 'Modéré' },
  { id: 'eleve', label: 'Elevé' },
  { id: 'imminent', label: 'Imminent' },
];

const getLabel = (id: string) => {
  return RISK_LEVELS.find(level => level.id === id)?.label || id;
};

export function JsonValue({ value }: JsonValueProps) {

  if (value === null || value === undefined || value === '') {
    return <EmptyValue />;
  }

  // Handle object case: {collet: "faible", tronc: "modere", houppier: "faible"}
  if (typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return <EmptyValue />;
    }

    return (
      <div className="space-y-2">
        {entries.map(([key, val]) => (
          <div
            key={key}
            className="flex items-center justify-between rounded-lg border border-border/40 bg-muted/30 px-3 py-2 dark:border-border/30 dark:bg-muted/20"
          >
            <span className="text-xs font-medium uppercase tracking-wide capitalize text-muted-foreground/90 dark:text-muted-foreground/80">
              {key}:
            </span>
            <span className="text-xs font-semibold text-foreground">
              {getLabel(val as string)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // Handle array case: [{collet: "faible"}, {tronc: "modere"}, {houppier: "faible"}]
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <EmptyValue />;
    }

    return (
      <div className="space-y-2">
        {value.map((item, index) => {
          // Handle case where item is an object with key-value pairs
          if (typeof item === 'object' && item !== null) {
            return Object.entries(item).map(([key, val]) => (
              <div
                key={`${index}-${key}`}
                className="flex items-center justify-between rounded-lg border border-border/40 bg-muted/30 px-3 py-2 dark:border-border/30 dark:bg-muted/20"
              >
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground/90 dark:text-muted-foreground/80">
                  {key}:
                </span>
                <span className="text-xs font-semibold text-foreground">{String(val)}</span>
              </div>
            ));
          }
          // Handle simple values
          return (
            <div
              key={index}
              className="rounded-lg border border-border/40 bg-muted/30 px-3 py-2 text-xs font-semibold text-foreground dark:border-border/30 dark:bg-muted/20"
            >
              {String(item)}
            </div>
          );
        })}
      </div>
    );
  }

  // Fallback for other types
  return <span className="text-sm font-semibold text-foreground">{String(value)}</span>;
}

